import { z } from "zod";
import { apiBuilder, prefixApi } from "@zodios/core";
import { datasetFilterSchema, dspDatasetsPaginationSchema, dspFrequencySchema, parseJson } from "./common";

const datasetsQueryRequestSchema = z.object({
  model: z.literal("dspcampaign"),
  dimensions: z.union([z.string(), z.array(z.string())]),
  entity_mode: z.union([z.literal("none"), z.literal("join"), z.literal("all"), z.literal("without_stats")]),
  filters: z.array(datasetFilterSchema).optional().default([]),
  sort: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  include_count: z.boolean().optional(),
});

const dspOrderEntityFields = {
  id: z.number(),
  delivery_status: z.string(),
  state: z.string(),
  name: z.string(),
  fees: z.string(),
  budget_caps: z.string(),
  optimization: z.string(),
  purchase_order_number: z.string(),
  last_updated_date_time: z.string(),
  creation_date_time: z.string(),
  flights: z.string(),
  frequencies: z.string(),
  trellis_managed: z.boolean(),
  advertiser_id: z.string(),
  comments: z.string(),
  campaign_id: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  day: z.string().optional(),
  week: z.string().optional(),
  month: z.string().optional(),
  flight_start_date: z.string().optional(),
  flight_end_date: z.string().optional(),
};

const dspOrderMetricFields = {
  impressions: z.number(),
  impressions_prev: z.number().nullable().optional(),
  impressions_percent: z.number().nullable().optional(),
  impressions_delta: z.number().nullable().optional(),

  clicks: z.number(),
  clicks_prev: z.number().nullable().optional(),
  clicks_percent: z.number().nullable().optional(),
  clicks_delta: z.number().nullable().optional(),

  sales: z.number(),
  sales_prev: z.number().nullable().optional(),
  sales_percent: z.number().nullable().optional(),
  sales_delta: z.number().nullable().optional(),

  total_sales: z.number(),
  total_sales_prev: z.number().nullable().optional(),
  total_sales_percent: z.number().nullable().optional(),
  total_sales_delta: z.number().nullable().optional(),

  total_cost: z.number(),
  total_cost_prev: z.number().nullable().optional(),
  total_cost_percent: z.number().nullable().optional(),
  total_cost_delta: z.number().nullable().optional(),

  total_detail_page_view: z.number(),
  total_detail_page_view_prev: z.number().nullable().optional(),
  total_detail_page_view_percent: z.number().nullable().optional(),
  total_detail_page_view_delta: z.number().nullable().optional(),

  total_purchases: z.number(),
  total_purchases_prev: z.number().nullable().optional(),
  total_purchases_percent: z.number().nullable().optional(),
  total_purchases_delta: z.number().nullable().optional(),

  brand_searches: z.number(),
  brand_searches_prev: z.number().nullable().optional(),
  brand_searches_percent: z.number().nullable().optional(),
  brand_searches_delta: z.number().nullable().optional(),

  new_to_brand_detail_page_views: z.number(),
  new_to_brand_detail_page_views_prev: z.number().nullable().optional(),
  new_to_brand_detail_page_views_percent: z.number().nullable().optional(),
  new_to_brand_detail_page_views_delta: z.number().nullable().optional(),

  total_add_to_cart: z.number(),
  total_add_to_cart_prev: z.number().nullable().optional(),
  total_add_to_cart_percent: z.number().nullable().optional(),
  total_add_to_cart_delta: z.number().nullable().optional(),

  total_new_to_brand_dpvs: z.number(),
  total_new_to_brand_dpvs_prev: z.number().nullable().optional(),
  total_new_to_brand_dpvs_percent: z.number().nullable().optional(),
  total_new_to_brand_dpvs_delta: z.number().nullable().optional(),

  viewable_impressions: z.number(),
  viewable_impressions_prev: z.number().nullable().optional(),
  viewable_impressions_percent: z.number().nullable().optional(),
  viewable_impressions_delta: z.number().nullable().optional(),

  units_sold: z.number(),
  units_sold_prev: z.number().nullable().optional(),
  units_sold_percent: z.number().nullable().optional(),
  units_sold_delta: z.number().nullable().optional(),

  total_units_sold: z.number(),
  total_units_sold_prev: z.number().nullable().optional(),
  total_units_sold_percent: z.number().nullable().optional(),
  total_units_sold_delta: z.number().nullable().optional(),

  ecpm: z.number().nullable(),
  ecpm_prev: z.number().nullable().optional(),
  ecpm_percent: z.number().nullable().optional(),
  ecpm_delta: z.number().nullable().optional(),

  click_thru_rate: z.number().nullable(),
  click_thru_rate_prev: z.number().nullable().optional(),
  click_thru_rate_percent: z.number().nullable().optional(),
  click_thru_rate_delta: z.number().nullable().optional(),

  tacos: z.number().nullable(),
  tacos_prev: z.number().nullable().optional(),
  tacos_percent: z.number().nullable().optional(),
  tacos_delta: z.number().nullable().optional(),

  troas: z.number().nullable(),
  troas_prev: z.number().nullable().optional(),
  troas_percent: z.number().nullable().optional(),
  troas_delta: z.number().nullable().optional(),

  roas: z.number().nullable(),
  roas_prev: z.number().nullable().optional(),
  roas_percent: z.number().nullable().optional(),
  roas_delta: z.number().nullable().optional(),

  total_detail_page_view_rate: z.number().nullable(),
  total_detail_page_view_rate_prev: z.number().nullable().optional(),
  total_detail_page_view_rate_percent: z.number().nullable().optional(),
  total_detail_page_view_rate_delta: z.number().nullable().optional(),

  total_add_to_cart_rate: z.number().nullable(),
  total_add_to_cart_rate_prev: z.number().nullable().optional(),
  total_add_to_cart_rate_percent: z.number().nullable().optional(),
  total_add_to_cart_rate_delta: z.number().nullable().optional(),

  new_to_brand_detail_page_views_rate: z.number().nullable(),
  new_to_brand_detail_page_views_rate_prev: z.number().nullable().optional(),
  new_to_brand_detail_page_views_rate_percent: z.number().nullable().optional(),
  new_to_brand_detail_page_views_rate_delta: z.number().nullable().optional(),

  total_new_to_brand_dpv_rate: z.number().nullable(),
  total_new_to_brand_dpv_rate_prev: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_percent: z.number().nullable().optional(),
  total_new_to_brand_dpv_rate_delta: z.number().nullable().optional(),

  brand_searches_rate: z.number().nullable(),
  brand_searches_rate_prev: z.number().nullable().optional(),
  brand_searches_rate_percent: z.number().nullable().optional(),
  brand_searches_rate_delta: z.number().nullable().optional(),

  cost_per_detail_page_view: z.number().nullable(),
  cost_per_detail_page_view_prev: z.number().nullable().optional(),
  cost_per_detail_page_view_percent: z.number().nullable().optional(),
  cost_per_detail_page_view_delta: z.number().nullable().optional(),

  viewability: z.number().nullable(),
  viewability_prev: z.number().nullable().optional(),
  viewability_percent: z.number().nullable().optional(),
  viewability_delta: z.number().nullable().optional(),
};

const dspFeesSchema = z.object({
  agencyFeePercentage: z.number().optional(),
});

const dspBudgetCapsSchema = z.array(z.unknown());

const dspOptimizationSchema = z.object({
  automateBidAllocation: z.boolean(),
  automateBudgetAllocation: z.boolean(),
  bidStrategy: z.string(),
  flightBudgetRolloverSetting: z
    .object({
      rolloverStrategy: z.string(),
    })
    .optional(),
  goalSetting: z.object({
    goal: z.string(),
    kpi: z.string(),
    targetKpi: z.number().optional(),
  }),
  primaryInventoryTypes: z.array(z.string()).optional(),
});

const dspFlightSchema = z.object({
  budgetAmount: z.number(),
  currencyCode: z.string(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
  flightId: z.string(),
});

const dspOrderSchema = z
  .object({
    ...dspOrderEntityFields,
    ...dspOrderMetricFields,
  })
  .transform((order) => ({
    ...order,
    fees: parseJson(dspFeesSchema, order.fees, null),
    budget_caps: parseJson(dspBudgetCapsSchema, order.budget_caps, []),
    optimization: parseJson(dspOptimizationSchema, order.optimization, null),
    flights: parseJson(z.array(dspFlightSchema), order.flights, []),
    frequencies: parseJson(z.array(dspFrequencySchema), order.frequencies, []),
  }));

const datasetsQueryResponseSchema = z.object({
  data: z.array(dspOrderSchema),
  pagination: dspDatasetsPaginationSchema,
});

export type DSPOrder = z.infer<typeof dspOrderSchema>;
export type DSPFlight = z.infer<typeof dspFlightSchema>;

export const dspOrderDatasetsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    method: "post",
    path: "/datasets/query/",
    alias: "dspOrderDatasetsQuery",
    description: "Query datasets by model + dimensions + filters",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: datasetsQueryRequestSchema,
      },
    ],
    response: datasetsQueryResponseSchema,
  }).build(),
);
